import { FieldProps } from "formik";
import _ from "lodash";
import { useCallback } from "react";
import { Label } from "@/components/ui/label";
import { inputVariants } from "@/constants/animations.ts";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface FormikTextareaProps {
    formikField: FieldProps;
    label?: string;
    placeholder?: string;
    rows?: number;
    onChange?: (value: string) => void;
    className?: string;
    disabled?: boolean;
}

const FormikTextarea = (props: FormikTextareaProps) => {
    const {
        formikField,
        onChange,
        label,
        placeholder,
        rows = 3,
        className = "",
        disabled = false,
        ...otherProps
    } = props;

    const { field, form } = formikField;
    const { name, value } = field;
    const { setFieldTouched, touched, errors, setFieldValue } = form;
    const hasError = _.get(touched, name) && !!_.get(errors, name);

    const textChangeHandler = useCallback((text: string) => {
        setFieldValue(name, text);
        setFieldTouched(name);
        if (onChange) {
            onChange(text);
        }
    }, [setFieldValue, setFieldTouched, name, onChange]);

    return (
        <motion.div 
            variants={inputVariants}
            initial="initial"
            animate="animate"
            className={`form-group ${className}`}
        >
            {label && (
                <Label htmlFor={name} className="block mb-2">
                    {label}
                </Label>
            )}
            <textarea
                id={name}
                name={name}
                value={value}
                onChange={(e) => textChangeHandler(e.target.value)}
                placeholder={placeholder}
                rows={rows}
                disabled={disabled}
                className={cn(
                    "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 resize-vertical transition-colors",
                    "bg-background text-foreground placeholder:text-muted-foreground",
                    hasError 
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500" 
                        : "border-input focus:border-transparent focus:ring-[var(--input-highlight-border)] focus:ring-[0.25px]"
                )}
                {...otherProps}
            />
            {hasError && (
                <div className="error-message text-red-500 text-sm mt-1">
                    {_.get(errors, name) as string}
                </div>
            )}
        </motion.div>
    );
};

export default FormikTextarea; 